/**
 * MyCalendar (2026-10-01) — `/calendar`: lịch kiểm tra, lịch thi, buổi học live,
 * phiên bảo vệ khoá luận của người đăng nhập. Lịch tháng (AntD Calendar) +
 * danh sách sự kiện của ngày đang chọn / sắp diễn ra.
 */
import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Calendar,
  Card,
  Col,
  Empty,
  List,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { CalendarOutlined, EyeOutlined, PlayCircleOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { type CalendarEvent, type CalendarEventType, useMyCalendar } from "@/api/calendar";

const { Title, Text } = Typography;

const TYPE_META: Record<CalendarEventType, { label: string; color: string }> = {
  exam: { label: "Thi (có giám sát)", color: "red" },
  quiz: { label: "Kiểm tra", color: "blue" },
  live: { label: "Buổi học live", color: "green" },
  defense: { label: "Bảo vệ khoá luận", color: "purple" },
};

const LIVE_STATUS: Record<string, string> = {
  scheduled: "Đã lên lịch",
  live: "Đang diễn ra",
  ended: "Đã kết thúc",
  canceled: "Đã huỷ",
};

function fmt(d: string) {
  return dayjs(d).format("HH:mm DD/MM");
}

/** Sự kiện có "chạm" ngày `day` không (bài kiểm tra mở nhiều ngày → hiện ở ngày mở + ngày đóng). */
function eventDays(e: CalendarEvent): string[] {
  const start = dayjs(e.start);
  // Bài kiểm tra kéo dài nhiều ngày: chỉ đánh dấu ngày mở và ngày đóng, tránh phủ kín lịch.
  const end = e.end ? dayjs(e.end).subtract(1, "second") : start;
  const days = [start.format("YYYY-MM-DD")];
  if (!end.isSame(start, "day")) days.push(end.format("YYYY-MM-DD"));
  return days;
}

/** Nút hành động: bài kiểm tra/thi → "Vào làm bài" (chỉ khi đang trong giờ làm,
 * sinh viên) hoặc "Xem bài" (giảng viên); buổi live/bảo vệ → "Vào phòng". */
function EventAction({ e }: { e: CalendarEvent }) {
  const navigate = useNavigate();
  const role = useAuthStore((st) => st.user?.role);
  const isStudent = role === "student";
  const now = dayjs();
  const started = !dayjs(e.start).isAfter(now);
  const ended = !!e.end && dayjs(e.end).isBefore(now);

  if (e.type === "exam" || e.type === "quiz") {
    if (!isStudent) {
      return (
        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(e.url)}>
          Xem bài
        </Button>
      );
    }
    return (
      <Button
        size="small"
        type="primary"
        icon={<PlayCircleOutlined />}
        disabled={!started || ended}
        onClick={() => navigate(e.url)}
        data-testid={`calendar-start-${e.id}`}
      >
        {!started ? "Chưa đến giờ làm bài" : ended ? "Đã hết giờ làm bài" : "Vào làm bài"}
      </Button>
    );
  }
  if (e.status === "canceled" || e.status === "ended" || ended) return null;
  return (
    <Button size="small" icon={<PlayCircleOutlined />} onClick={() => navigate(e.url)}>
      Vào phòng
    </Button>
  );
}

function EventItem({ e }: { e: CalendarEvent }) {
  const meta = TYPE_META[e.type];
  return (
    <List.Item actions={[<EventAction key="act" e={e} />]}>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Space wrap>
          <Tag color={meta.color}>{meta.label}</Tag>
          <Link to={e.url}>
            <Text strong>{e.title}</Text>
          </Link>
          {e.status && e.status !== "scheduled" && <Tag>{LIVE_STATUS[e.status] ?? e.status}</Tag>}
        </Space>
        {e.course && (
          <Text type="secondary">
            {e.course.code} — {e.course.name}
          </Text>
        )}
        <Text>
          {fmt(e.start)}
          {e.end ? ` → ${fmt(e.end)}` : ""}
        </Text>
      </Space>
    </List.Item>
  );
}

export function MyCalendar() {
  const [selected, setSelected] = useState<Dayjs>(dayjs());
  const [panel, setPanel] = useState<Dayjs>(dayjs());

  // Tải cả tháng đang xem (± 7 ngày cho các ô tràn đầu/cuối lưới lịch).
  const from = panel.startOf("month").subtract(7, "day").format("YYYY-MM-DD");
  const to = panel.endOf("month").add(7, "day").format("YYYY-MM-DD");
  const { data, isLoading, error } = useMyCalendar(from, to);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of data?.events ?? []) {
      for (const d of eventDays(e)) {
        if (!map.has(d)) map.set(d, []);
        map.get(d)!.push(e);
      }
    }
    return map;
  }, [data]);

  const dayEvents = (data?.events ?? []).filter((e) => {
    // Sự kiện đang diễn ra trong ngày được chọn (kể cả bài mở nhiều ngày).
    const s = dayjs(e.start);
    const en = e.end ? dayjs(e.end) : s;
    return !s.isAfter(selected.endOf("day")) && !en.isBefore(selected.startOf("day"));
  });
  // Đang diễn ra = đã bắt đầu + chưa kết thúc (bài đang mở, buổi live đang chạy);
  // Sắp diễn ra = CHƯA bắt đầu. Trước đây gộp chung → bài đang mở nằm ở "Sắp diễn ra".
  const now = dayjs();
  const ongoing = (data?.events ?? []).filter(
    (e) => !dayjs(e.start).isAfter(now) && (e.end ? dayjs(e.end).isAfter(now) : false),
  );
  const upcoming = (data?.events ?? []).filter((e) => dayjs(e.start).isAfter(now)).slice(0, 8);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }} data-testid="my-calendar">
      <Title level={2}>
        <CalendarOutlined /> Lịch của tôi
      </Title>
      <Space wrap style={{ marginBottom: 16 }}>
        {Object.entries(TYPE_META).map(([k, m]) => (
          <Badge key={k} color={m.color} text={m.label} />
        ))}
      </Space>
      {error && (
        <Alert type="error" showIcon message="Không tải được lịch." style={{ marginBottom: 16 }} />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card>
            <Spin spinning={isLoading}>
              <Calendar
                value={selected}
                onSelect={(d) => setSelected(d)}
                onPanelChange={(d) => setPanel(d)}
                cellRender={(current, info) => {
                  if (info.type !== "date") return info.originNode;
                  const items = byDay.get(current.format("YYYY-MM-DD")) ?? [];
                  return (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                      {items.slice(0, 3).map((e) => (
                        <li
                          key={e.id}
                          style={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Badge
                            color={TYPE_META[e.type].color}
                            text={<span style={{ fontSize: 12 }}>{e.title}</span>}
                          />
                        </li>
                      ))}
                      {items.length > 3 && (
                        <li>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            +{items.length - 3} sự kiện
                          </Text>
                        </li>
                      )}
                    </ul>
                  );
                }}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Card title={`Ngày ${selected.format("DD/MM/YYYY")}`} size="small">
              {dayEvents.length === 0 ? (
                <Empty description="Không có lịch" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List dataSource={dayEvents} renderItem={(e) => <EventItem e={e} />} />
              )}
            </Card>
            {ongoing.length > 0 && (
              <Card
                title={`Đang diễn ra (${ongoing.length})`}
                size="small"
                data-testid="calendar-ongoing"
              >
                <List dataSource={ongoing} renderItem={(e) => <EventItem e={e} />} />
              </Card>
            )}
            <Card title="Sắp diễn ra" size="small">
              {upcoming.length === 0 ? (
                <Empty description="Không có lịch sắp tới" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List dataSource={upcoming} renderItem={(e) => <EventItem e={e} />} />
              )}
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
}

export default MyCalendar;
